import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Card,
  Chip,
  Collapse,
  Grid,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import CascadingSidePanel from './CascadingSidePanel';
import DetailRow from '../../form/DetailRow';
import { useDispatch } from 'react-redux';
import { closeSiteSidePanel } from '../../store/slices/sidePanelSlice';

interface Site {
  id: string;
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  status?: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  orders?: any[];
  totalOrders?: number;
  completedOrders?: number;
  pendingOrders?: number;
}

interface Customer {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
}

interface SiteSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseAll?: () => void;
  sites: Site[];
  onSiteSelect?: (site: Site) => void;
  title?: string;
  loading?: boolean;
  customer?: Customer;
  onBackToCustomers?: () => void;
  selectedSiteId?: string; // Track selected site
}

const SiteSidePanel: React.FC<SiteSidePanelProps> = ({
  isOpen,
  onClose,
  onCloseAll,
  sites,
  onSiteSelect,
  title = "Sites",
  loading = false,
  customer,
  onBackToCustomers,
  selectedSiteId
}) => {
  const dispatch = useDispatch();
  console.log('SiteSidePanel - sites:', sites);
  const [customerDetailsExpanded, setCustomerDetailsExpanded] = useState(false);
  const [showAllDetails, setShowAllDetails] = useState(false);

  const capitalizeWords = (str: string) => {
    return str.replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <CascadingSidePanel
      isOpen={isOpen}
      onClose={onClose}
      onCloseAll={onCloseAll}
      panelId="site-panel"
      title={title}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
          {/* Header - Collapsible */}
          <Box sx={{ 
            borderBottom: '1px solid', 
            borderColor: 'divider'
          }}>
            {/* Main Header - Clickable when customer exists */}
            <Box 
              sx={{ 
                p: 2, 
                cursor: customer ? 'pointer' : 'default',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                '&:hover': customer ? {
                  bgcolor: 'action.hover'
                } : {}
              }}
              onClick={customer ? () => setCustomerDetailsExpanded(!customerDetailsExpanded) : undefined}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {onBackToCustomers && (
                  <IconButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      onBackToCustomers();
                    }}
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    ←
                  </IconButton>
                )}
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {customer?.name}
                </Typography>
                {customer && (
                  <IconButton 
                    size="small" 
                    sx={{ color: 'text.secondary' }}
                  >
                    {customerDetailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                )}
              </Box>
              <IconButton 
                // onClick={onCloseAll || onClose}
                onClick={() => {
                  dispatch(closeSiteSidePanel())
                }}
                size="small"
              >
                ✕
              </IconButton>
            </Box>

            {/* Collapsible Customer Details */}
            {customer && (
              <Collapse in={customerDetailsExpanded}>
                <Box sx={{ 
                  p: 2, 
                  pt: 0,
                  // bgcolor: 'primary.light', 
                  // color: 'primary.contrastText',
                  borderRadius: '0 0 8px 8px'
                }}>
                  {/* <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {customer.name}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {customer.email && (
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                        Email: {customer.email}
                      </Typography>
                    )}
                    {customer.phone && (
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                        Phone: {customer.phone}
                      </Typography>
                    )}
                    {(customer.address || customer.city || customer.state) && (
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                        {[customer.address, customer.city, customer.state].filter(Boolean).join(', ')}
                      </Typography>
                    )}
                  </Box> */}
                  <Grid 
                        container 
                        spacing={2} 
                        sx={{ mt: 1 }}
                      >
                        
                        {/* {customer.name &&(<Grid item xs={12} sm={6}>
                          <DetailRow label="Name" value={customer?.name} />
                        </Grid>
                        )} */}
                        {/* <Grid item xs={12} sm={6}>
                          <DetailRow label="Name" value={customer?.name} />
                        </Grid> */}
                        {customer.phone &&(<Grid item xs={12} sm={6}>
                          <DetailRow label="Phone" value={customer?.phone} />
                        </Grid>
                        )}
          
                        {customer.email &&(<Grid item xs={12} sm={6}>
                          <DetailRow label="Email" value={customer?.email} />
                        </Grid>
                        )}             
                        {(customer.city && customer.state) &&(<Grid item xs={12} sm={6}>
                          <DetailRow 
                            label="Address" 
                            value={`${customer?.city || ""}, ${customer?.state || ""}`} 
                          />
                        </Grid>
                        )}
          
                        {customer.contactPerson &&(<Grid item xs={12} sm={6}>
                          <DetailRow label="Contact Person" value={customer?.contactPerson} />
                        </Grid>
                        )}
                        {customer.contactPhone &&(<Grid item xs={12} sm={6}>
                          <DetailRow label="Contact Phone" value={customer?.contactPhone} />
                        </Grid>
                        )}
          
                        {customer.contactEmail &&(<Grid item xs={12} sm={6}>
                          <DetailRow label="Contact Email" value={customer?.contactEmail} />
                        </Grid>
                        )}
                
                      </Grid>
                </Box>
              </Collapse>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600,mb:1 }}>
              Site List
            </Typography>
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '200px',
                textAlign: 'center'
              }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Loading sites...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we fetch the sites for this customer.
                </Typography>
              </Box>
            ) : sites.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '200px',
                textAlign: 'center'
              }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No sites found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This customer doesn't have any sites yet.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {sites.map((site, index) => {
                  const isSelected = selectedSiteId === site.id;
                  return (
                    <Card 
                      key={site.id || index}
                      sx={{ 
                        p: 2, 
                        cursor: 'pointer',
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        backgroundColor: isSelected ? 'primary.lighter' : 'transparent',
                        '&:hover': {
                          backgroundColor: isSelected ? 'primary.lighter' : 'action.hover',
                          transform: 'translateY(-1px)',
                          boxShadow: isSelected ? 3 : 1,
                          borderColor: 'primary.main'
                        },
                        transition: 'all 0.2s ease-in-out',
                        boxShadow: isSelected ? 2 : 0,
                      }}
                      onClick={() => onSiteSelect?.(site)}
                    >
                      

                      {/* Site Header with Name, Orders, and Status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            bgcolor: 'primary.main',
                            flexShrink: 0
                          }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                            { capitalizeWords(site.name) || `Site ${index + 1}`} - ({site?.namePlate || 'N/A'})
                          </Typography>
                          {/* Order Count Badge */}
                          {(site.orders || site.totalOrders) && (
                            <Chip
                              label={`${site.totalOrders || site.orders?.length || 0} Orders`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ 
                                fontSize: '0.65rem', 
                                height: 20,
                                fontWeight: 600
                              }}
                            />
                          )}
                        </Box>
                        <Chip
                          label={capitalizeWords(site.status || 'active')}
                          color={site.status === 'active' ? 'success' : 
                                 site.status === 'inactive' ? 'default' : 'warning'}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>

                      {/* Site Details - Header Format */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Address Row */}
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: "text.secondary",
                              mb: 0.5
                            }}
                          >
                            Address
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontSize: 14, fontWeight: 400 }}
                          >
                            {site.address ? `${site.address}, ${site.city || ''}, ${site.state || ''}`.replace(/,\s*,/g, ',').replace(/,\s*$/, '') : 'N/A'}
                          </Typography>
                        </Box>

                        {/* Contact Person and Phone - headers on top, values exactly under respective headers */}
                        <Box>
                          <Box sx={{ display: 'flex', gap: 3 }}>
                            <Typography variant="body2" sx={{ flex: 1, fontSize: 12, color: 'text.secondary', fontWeight: 700 }}>
                              Contact Person
                            </Typography>
                            <Typography variant="body2" sx={{ flex: 1, fontSize: 12, color: 'text.secondary', fontWeight: 700 }}>
                              Contact Phone
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 3 }}>
                            <Typography variant="body2" sx={{ flex: 1, fontSize: 12 }}>
                              <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{site.contactPerson || 'N/A'}</Box>
                            </Typography>
                            <Typography variant="body2" sx={{ flex: 1, fontSize: 12 }}>
                              <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{site?.contactPhone || 'N/A'}</Box>
                            </Typography>
                          </Box>
                        </Box>

                        {/* Additional Info Row */}
                        {(site.email || site.orders || site.totalOrders) && (
                          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            {site.email && (
                              <Box sx={{ flex: '1', minWidth: '150px' }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "text.secondary",
                                    mb: 0.5
                                  }}
                                >
                                  Email
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontSize: 14, fontWeight: 400 }}
                                >
                                  {site.email}
                                </Typography>
                              </Box>
                            )}
                            {(site.orders || site.totalOrders) && (
                              <Box sx={{ flex: '1', minWidth: '150px' }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "text.secondary",
                                    mb: 0.5
                                  }}
                                >
                                  Orders
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontSize: 14, fontWeight: 400 }}
                                >
                                  {site.totalOrders || site.orders?.length || 0}
                                </Typography>
                                {(site.completedOrders || site.pendingOrders) && (
                                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                    {site.completedOrders && (
                                      <Typography
                                        variant="caption"
                                        sx={{ 
                                          fontSize: '0.7rem',
                                          color: 'success.main',
                                          fontWeight: 500
                                        }}
                                      >
                                        Completed: {site.completedOrders}
                                      </Typography>
                                    )}
                                    {site.pendingOrders && (
                                      <Typography
                                        variant="caption"
                                        sx={{ 
                                          fontSize: '0.7rem',
                                          color: 'warning.main',
                                          fontWeight: 500
                                        }}
                                      >
                                        Pending: {site.pendingOrders}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
    </CascadingSidePanel>
  );
};

export default SiteSidePanel;
